import type { TenantScope } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import type { WithoutEnvironments } from '../utils/scope-environments';

// These aren't sent to the API - just used to represent all the options of decryptable things in
// the MultiSelect. We need this since different decrypt options have special serializations in the API
export enum DecryptOption {
  all = 'all',
  name = 'name',
  email = 'email',
  phoneNumber = 'phone_number',
  usTaxId = 'us_tax_id',
  ssn4 = 'ssn4',
  dob = 'dob',
  documents = 'documents',
  documentsAndSelfie = 'documents_and_selfie',
  fullAddress = 'full_address',
  custom = 'custom',
  investorProfile = 'investor_profile',
  usLegalStatus = 'us_legal_status',
  card = 'card',
  allExceptPciData = 'all_except_pci_data',
  businessName = 'business_name',
  businessTin = 'business_tin',
  businessAddress = 'business_address',
  businessPhoneNumber = 'business_phone_number',
  businessWebsite = 'business_website',
  businessKycedBeneficialOwners = 'business_kyced_beneficial_owners',
  businessCorporationType = 'business_corporation_type',
  businessFormationInfo = 'business_formation_info',
}

export const decryptOptionFromTenantScope = (scope: TenantScope): DecryptOption | undefined => {
  if (scope.kind === 'decrypt_all') {
    return DecryptOption.all;
  }
  if (scope.kind === 'decrypt_all_except_pci_data') {
    return DecryptOption.allExceptPciData;
  }
  if (scope.kind === 'decrypt_custom') {
    return DecryptOption.custom;
  }
  if (scope.kind === 'decrypt_document') {
    return DecryptOption.documents;
  }
  if (scope.kind === 'decrypt_document_and_selfie') {
    return DecryptOption.documentsAndSelfie;
  }
  if (scope.kind === 'decrypt') {
    if (scope.data === 'email') {
      return DecryptOption.email;
    }
    if (scope.data === 'us_tax_id') {
      return DecryptOption.usTaxId;
    }
    if (scope.data === 'dob') {
      return DecryptOption.dob;
    }
    if (scope.data === 'full_address') {
      return DecryptOption.fullAddress;
    }
    if (scope.data === 'us_legal_status') {
      return DecryptOption.usLegalStatus;
    }
    if (scope.data === 'card') {
      return DecryptOption.card;
    }
    if (scope.data === 'investor_profile') {
      return DecryptOption.investorProfile;
    }
    if (scope.data === 'name') {
      return DecryptOption.name;
    }
    if (scope.data === 'phone_number') {
      return DecryptOption.phoneNumber;
    }
    if (scope.data === 'ssn4') {
      return DecryptOption.ssn4;
    }
    if (scope.data === 'business_name') {
      return DecryptOption.businessName;
    }
    if (scope.data === 'business_tin') {
      return DecryptOption.businessTin;
    }
    if (scope.data === 'business_address') {
      return DecryptOption.businessAddress;
    }
    if (scope.data === 'business_phone_number') {
      return DecryptOption.businessPhoneNumber;
    }
    if (scope.data === 'business_website') {
      return DecryptOption.businessWebsite;
    }
    if (scope.data === 'business_kyced_beneficial_owners') {
      return DecryptOption.businessKycedBeneficialOwners;
    }
    if (scope.data === 'business_corporation_type') {
      return DecryptOption.businessCorporationType;
    }
    if (scope.data === 'business_formation_info') {
      return DecryptOption.businessFormationInfo;
    }
  }
  return undefined;
};

export const DecryptOptionToTenantScope: Record<DecryptOption, WithoutEnvironments<TenantScope>> = {
  [DecryptOption.all]: { kind: 'decrypt_all' },
  [DecryptOption.name]: { kind: 'decrypt', data: 'name' },
  [DecryptOption.email]: { kind: 'decrypt', data: 'email' },
  [DecryptOption.phoneNumber]: { kind: 'decrypt', data: 'phone_number' },
  [DecryptOption.usTaxId]: { kind: 'decrypt', data: 'us_tax_id' },
  [DecryptOption.ssn4]: { kind: 'decrypt', data: 'ssn4' },
  [DecryptOption.dob]: { kind: 'decrypt', data: 'dob' },
  [DecryptOption.documents]: { kind: 'decrypt_document' },
  [DecryptOption.documentsAndSelfie]: { kind: 'decrypt_document_and_selfie' },
  [DecryptOption.fullAddress]: { kind: 'decrypt', data: 'full_address' },
  [DecryptOption.usLegalStatus]: { kind: 'decrypt', data: 'us_legal_status' },
  [DecryptOption.custom]: { kind: 'decrypt_custom' },
  [DecryptOption.investorProfile]: { kind: 'decrypt', data: 'investor_profile' },
  [DecryptOption.card]: { kind: 'decrypt', data: 'card' },
  [DecryptOption.allExceptPciData]: { kind: 'decrypt_all_except_pci_data' },
  [DecryptOption.businessName]: { kind: 'decrypt', data: 'business_name' },
  [DecryptOption.businessCorporationType]: { kind: 'decrypt', data: 'business_corporation_type' },
  [DecryptOption.businessTin]: { kind: 'decrypt', data: 'business_tin' },
  [DecryptOption.businessWebsite]: { kind: 'decrypt', data: 'business_website' },
  [DecryptOption.businessPhoneNumber]: { kind: 'decrypt', data: 'business_phone_number' },
  [DecryptOption.businessAddress]: { kind: 'decrypt', data: 'business_address' },
  [DecryptOption.businessKycedBeneficialOwners]: { kind: 'decrypt', data: 'business_kyced_beneficial_owners' },
  [DecryptOption.businessFormationInfo]: { kind: 'decrypt', data: 'business_formation_info' },
};

type Option = {
  label: string;
  value: DecryptOption;
};

type Group = {
  options: readonly Option[];
  label?: string;
};

const useDecryptOptions = () => {
  const { t } = useTranslation('roles', { keyPrefix: 'scopes' });

  const options: Group[] = [
    {
      label: 'User identity data',
      options: [
        {
          value: DecryptOption.name,
          label: t($ => $.decrypt.name),
        },
        {
          value: DecryptOption.dob,
          label: t($ => $.decrypt.dob),
        },
        {
          value: DecryptOption.usTaxId,
          label: t($ => $.decrypt.us_tax_id),
        },
        {
          value: DecryptOption.ssn4,
          label: t($ => $.decrypt.ssn4),
        },
        {
          value: DecryptOption.fullAddress,
          label: t($ => $.decrypt.full_address),
        },
        {
          value: DecryptOption.email,
          label: t($ => $.decrypt.email),
        },
        {
          value: DecryptOption.phoneNumber,
          label: t($ => $.decrypt.phone_number),
        },
      ],
    },
    {
      label: 'Other user data',
      options: [
        {
          value: DecryptOption.documentsAndSelfie,
          label: t($ => $.decrypt.documents_and_selfie),
        },
        {
          value: DecryptOption.documents,
          label: t($ => $.decrypt.documents),
        },
        {
          value: DecryptOption.custom,
          label: t($ => $.decrypt.custom),
        },
        {
          value: DecryptOption.investorProfile,
          label: t($ => $.decrypt.investor_profile),
        },
        {
          value: DecryptOption.usLegalStatus,
          label: t($ => $.decrypt.us_legal_status),
        },
        {
          value: DecryptOption.card,
          label: t($ => $.decrypt.card),
        },
      ],
    },
    {
      label: 'Business data',
      options: [
        {
          value: DecryptOption.businessName,
          label: t($ => $.decrypt.business_name),
        },
        {
          value: DecryptOption.businessFormationInfo,
          label: t($ => $.decrypt.business_formation_info),
        },
        {
          value: DecryptOption.businessCorporationType,
          label: t($ => $.decrypt.business_corporation_type),
        },
        {
          value: DecryptOption.businessTin,
          label: t($ => $.decrypt.business_tin),
        },
        {
          value: DecryptOption.businessAddress,
          label: t($ => $.decrypt.business_address),
        },
        {
          value: DecryptOption.businessWebsite,
          label: t($ => $.decrypt.business_website),
        },
        {
          value: DecryptOption.businessPhoneNumber,
          label: t($ => $.decrypt.business_phone_number),
        },
        {
          value: DecryptOption.businessKycedBeneficialOwners,
          label: t($ => $.decrypt.business_kyced_beneficial_owners),
        },
      ],
    },
  ];

  const allOption = {
    value: DecryptOption.all,
    label: t($ => $.decrypt_all),
  };

  const allExceptPciDataOption = {
    value: DecryptOption.allExceptPciData,
    label: t($ => $.decrypt_all_except_pci_data),
  };

  const labels = new Map(
    [allOption, allExceptPciDataOption, ...options.flatMap(group => group.options)].map(option => [
      option.value,
      option.label,
    ]),
  );
  const getLabel = (option: DecryptOption) => labels.get(option) ?? option;

  return { options, allOption, allExceptPciDataOption, getLabel };
};

export default useDecryptOptions;
